// Single source of truth for E2E test-id hooks.
// Imported by Vue components (to render data-testid) and by the Playwright
// page objects (to query them), so a renamed id can never drift between the
// two. CommonJS so both the webpack build and Playwright's loader can consume
// it without an ESM/CJS mismatch.

const TEST_IDS = {
  app: {
    root: 'app-root',
    loader: 'app-loader',
  },
  login: {
    email: 'login-email',
    password: 'login-password',
    submit: 'login-submit',
    error: 'login-error',
  },
  nav: {
    userMenu: 'nav-user-menu',
    logout: 'nav-logout',
  },
  reports: {
    inactiveMembers: {
      gender: 'inactive-members-gender-filter',
    },
  },
  member: {
    addButton: 'member-add-button',
    searchInput: 'member-search-input',
    editButton: 'member-edit-button',
    dropdownMenu: 'member-dropdown-menu',
    row: 'member-row',
    // One per table row; disambiguate by text/data-member-id at query time.
    rowLink: 'member-row-link',
    form: {
      firstName: 'member-first-name',
      middleName: 'member-middle-name',
      lastName: 'member-last-name',
      idNumber: 'member-id-number',
      email: 'member-email',
      phonePrimary: 'member-phone-primary',
      photoInput: 'member-photo-input',
      selfServiceCheckbox: 'member-self-service-checkbox',
      save: 'member-save',
      saveAndMembership: 'member-save-and-membership',
      cancel: 'member-cancel',
    },
  },
  membershipType: {
    addButton: 'membership-type-add-button',
    searchInput: 'membership-type-search-input',
    row: 'membership-type-row',
    idAttribute: 'data-membership-type-id',
    form: {
      name: 'membership-type-name',
      description: 'membership-type-description',
      fees: 'membership-type-fees',
      status: 'membership-type-status',
      periodLengthInDays: 'membership-type-period-length-days',
      save: 'membership-type-save',
      cancel: 'membership-type-cancel',
    },
  },
  membership: {
    addButton: 'membership-add-button',
    bulkActionsMenu: 'membership-bulk-actions-menu',
    extendDaysMenuItem: 'membership-extend-days-menu-item',
    row: 'membership-row',
    rowCheckbox: 'membership-row-checkbox',
    idAttribute: 'data-membership-id',
    extendDays: {
      numberOfDays: 'membership-extend-days-number',
      note: 'membership-extend-days-note',
      confirm: 'membership-extend-days-confirm',
      ok: 'membership-extend-days-ok',
    },
    form: {
      startDate: 'membership-start-date',
      membershipType: 'membership-type-select',
      periodLengthInDays: 'membership-period-length-days',
      fees: 'membership-fees',
      status: 'membership-status',
      save: 'membership-save',
      cancel: 'membership-cancel',
    },
  },
  payment: {
    newMenuButton: 'payment-new-menu-button',
    createPaymentMenuItem: 'payment-create-payment-menu-item',
    row: 'payment-row',
    idAttribute: 'data-payment-id',
    form: {
      membership: 'payment-membership-select',
      amount: 'payment-amount',
      status: 'payment-status',
      method: 'payment-method',
      save: 'payment-save',
      cancel: 'payment-cancel',
    },
  },
  inventory: {
    searchInput: 'inventory-search-input',
    row: 'inventory-row',
    dropdownMenu: 'inventory-dropdown-menu',
    addQuantityButton: 'inventory-add-quantity-button',
    idAttribute: 'data-inventory-product-id',
    form: {
      quantity: 'inventory-quantity',
      note: 'inventory-note',
      unitPrice: 'inventory-unit-price',
      save: 'inventory-save',
      cancel: 'inventory-cancel',
    },
  },
  cashbox: {
    addButton: 'cashbox-add-button',
    row: 'cashbox-row',
    idAttribute: 'data-cashbox-id',
    form: {
      name: 'cashbox-name',
      branch: 'cashbox-branch',
      save: 'cashbox-save',
      cancel: 'cashbox-cancel',
    },
  },
  vendor: {
    addButton: 'vendor-add-button',
    row: 'vendor-row',
    rowLink: 'vendor-row-link',
    idAttribute: 'data-vendor-id',
    form: {
      name: 'vendor-name',
      phone: 'vendor-phone',
      address: 'vendor-address',
      save: 'vendor-save',
      cancel: 'vendor-cancel',
    },
  },
  product: {
    addButton: 'product-add-button',
    searchInput: 'product-search-input',
    dropdownMenu: 'product-dropdown-menu',
    editButton: 'product-edit-button',
    row: 'product-row',
    idAttribute: 'data-product-id',
    form: {
      name: 'product-name',
      price: 'product-price',
      category: 'product-category',
      barcode: 'product-barcode',
      availableFrom: 'product-available-from',
      availableTo: 'product-available-to',
      productForSale: 'product-for-sale',
      enableInventory: 'product-enable-inventory',
      enableStockManagement: 'product-enable-stock-management',
      allowDiscount: 'product-allow-discount',
      discountType: 'product-discount-type',
      discountValue: 'product-discount-value',
      autoApplyDiscount: 'product-auto-apply-discount',
      imageInput: 'product-image-input',
      save: 'product-save',
      cancel: 'product-cancel',
    },
  },
  pos: {
    searchInput: 'pos-product-search-input',
    productCard: 'pos-product-card',
    cartRow: 'pos-cart-row',
    confirmButton: 'pos-confirm-button',
    transactionRow: 'pos-transaction-row',
    transactionOrderNumber: 'pos-transaction-order-number',
    lineDiscountToggle: 'pos-line-discount-toggle',
    lineDiscountType: 'pos-line-discount-type',
    lineDiscountValue: 'pos-line-discount-value',
    orderDiscountToggle: 'pos-order-discount-toggle',
    orderDiscountType: 'pos-order-discount-type',
    orderDiscountValue: 'pos-order-discount-value',
    subtotal: 'pos-subtotal',
    productDiscountTotal: 'pos-product-discount-total',
    orderDiscountAmount: 'pos-order-discount-amount',
    productIdAttribute: 'data-pos-product-id',
    saleOrderIdAttribute: 'data-pos-sale-order-id',
  },
  sales: {
    row: 'sales-row',
    posOrderNumber: 'sales-pos-order-number',
    saleOrderIdAttribute: 'data-sales-sale-order-id',
  },
  receptionDesk: {
    checkInButton: 'reception-check-in-button',
    row: 'reception-member-log-row',
    memberLogIdAttribute: 'data-member-log-id',
    checkIn: {
      form: 'manual-check-in-form',
      memberSearch: 'manual-check-in-member-search',
      selectedMember: 'manual-check-in-selected-member',
      gate: 'manual-check-in-gate',
      membership: 'manual-check-in-membership',
      save: 'manual-check-in-save',
      cancel: 'manual-check-in-cancel',
    },
  },
  memberEntryPopup: {
    root: 'member-entry-popup',
    dismiss: 'member-entry-popup-dismiss',
    entryLogIdAttribute: 'data-member-entry-log-id',
  },
  expenseType: {
    addButton: 'expense-type-add-button',
    row: 'expense-type-row',
    idAttribute: 'data-expense-type-id',
    form: {
      name: 'expense-type-name',
      description: 'expense-type-description',
      save: 'expense-type-save',
      cancel: 'expense-type-cancel',
    },
  },
  expense: {
    addButton: 'expense-add-button',
    row: 'expense-row',
    idAttribute: 'data-expense-id',
    form: {
      type: 'expense-type-select',
      date: 'expense-date',
      paymentMethod: 'expense-payment-method',
      amount: 'expense-amount',
      description: 'expense-description',
      save: 'expense-save',
      cancel: 'expense-cancel',
    },
  },
  dateTimePicker: {
    popup: '.vdatetime-popup',
    selectableDay: '.vdatetime-calendar__month__day:not(.vdatetime-calendar__month__day--disabled)',
  },
};

module.exports = TEST_IDS;
